import { useState } from 'react';
import { D, st, toArr, apiGet, apiPost, calcTotal } from '../constants';
import { Overlay, MHdr, Field, getDirections } from './shared';
import FieldNotesPanel from './FieldNotesPanel';

const JOB_STATUSES = ['Needs Scheduling','Scheduled','In Progress','Completed','On Hold'];
const STATUS_COLOR = {"Needs Scheduling":"#E24B4A", Scheduled:"#378ADD", "In Progress":"#f0a030", Completed:"#1D9E75", "On Hold":"#888"};
const STATUS_BG = {"Needs Scheduling":"#FCEBEB", Scheduled:"#E6F1FB", "In Progress":"#FBF1E1", Completed:"#E1F5EE", "On Hold":"#F1EFE8"};

const JobBadge = ({status}) =>
  <span style={{background:STATUS_BG[status]||"#eee",color:STATUS_COLOR[status]||"#888",
    fontSize:11,fontWeight:500,padding:"2px 8px",borderRadius:6,
    border:`0.5px solid ${STATUS_COLOR[status]||"#ccc"}`}}>{status}</span>;

const emptyJob = () => ({
  title: '', clientId: null, estimateId: null, bidId: null, status: 'Scheduled',
  date: new Date().toISOString().slice(0,10), time: '', endDate: '',
  address: '', description: ''
});

export default function Jobs({jobs, clients, estimates, bids, expenses, invoices, markup, clientName, apiSaveJob, apiDeleteJob, apiSaveJobNotes, fmt, fmtDate, me, openClientModal, openInvoiceModal, calcTotal}) {
  const isOwner = me?.user?.role === 'owner';
  const canBill = isOwner || me?.user?.role === 'foreman';
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('Active');
  const [laborEntries, setLaborEntries] = useState([]);
  const [laborLoading, setLaborLoading] = useState(false);
  const [laborForm, setLaborForm] = useState(null);
  const [jobEmployees, setJobEmployees] = useState([]);
  const [logEntries, setLogEntries] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logForm, setLogForm] = useState(null);

  const items = toArr(jobs);
  const activeJobs = items.filter(j => j.status === 'Scheduled' || j.status === 'In Progress' || j.status === 'Needs Scheduling');
  const needsScheduling = items.filter(j => j.status === 'Needs Scheduling');
  const today = new Date().toISOString().slice(0,10);
  const todayJobs = items.filter(j => j.date === today && j.status !== 'Completed');

  const visible = items.filter(j => {
    if (filter === 'Active') return j.status === 'Scheduled' || j.status === 'In Progress' || j.status === 'Needs Scheduling';
    if (filter === 'All') return true;
    return j.status === filter;
  });

  const loadLabor = async (jobId) => {
    setLaborLoading(true);
    try {
      const [labor, emps] = await Promise.all([
        apiGet(`/employee/jobs/${jobId}/labor`),
        apiGet('/data/users')
      ]);
      setLaborEntries(Array.isArray(labor) ? labor : []);
      setJobEmployees(Array.isArray(emps) ? emps.filter(u => u.role !== 'owner' && u.is_active !== 0) : []);
    } catch(_) {}
    setLaborLoading(false);
  };
  const loadLog = async (jobId) => {
    setLogLoading(true);
    try {
      const entries = await apiGet(`/data/jobs/${jobId}/log`);
      setLogEntries(Array.isArray(entries) ? entries : []);
    } catch(_) {}
    setLogLoading(false);
  };

  const saveLabor = async () => {
    if (!laborForm.userId || !laborForm.hours) return;
    try {
      const emp = jobEmployees.find(e => e.id === Number(laborForm.userId));
      const entry = await apiPost('/data/payroll', {
        userId: Number(laborForm.userId),
        date: laborForm.date,
        hours: parseFloat(laborForm.hours) || 0,
        rate: parseFloat(laborForm.rate) || emp?.rate || 0,
        payType: 'Hourly',
        jobId: modal.id,
        notes: laborForm.notes || '',
      });
      setLaborEntries(prev => [...prev, {
        id: entry.id, user_id: Number(laborForm.userId),
        employee_name: emp?.name || 'Unknown',
        date: laborForm.date,
        hours: parseFloat(laborForm.hours) || 0,
        rate: parseFloat(laborForm.rate) || emp?.rate || 0,
        notes: laborForm.notes || '', pay_type: 'Hourly',
      }]);
      setLaborForm(null);
    } catch(e) { alert('Failed to save: ' + (e.message||e)); }
  };
  const saveLog = async () => {
    if (!logForm.description && !Number(logForm.masterHrs) && !Number(logForm.journeymanHrs) && !Number(logForm.apprenticeHrs)) return;
    try {
      const entry = await apiPost(`/data/jobs/${modal.id}/log`, {
        logDate: logForm.date,
        description: logForm.description,
        masterHrs: parseFloat(logForm.masterHrs)||0,
        journeymanHrs: parseFloat(logForm.journeymanHrs)||0,
        apprenticeHrs: parseFloat(logForm.apprenticeHrs)||0,
        materials: [],
      });
      setLogEntries(prev => [entry, ...prev]);
      setLogForm(null);
    } catch(e) { alert('Failed to save log: '+(e.message||e)); }
  };

  const deleteLog = async (id) => {
    if (!confirm('Delete this log entry?')) return;
    try {
      await apiPost(`/data/jobs/${modal.id}/log/${id}`, {}, 'DELETE');
      setLogEntries(prev => prev.filter(e => e.id !== id));
    } catch(e) { alert('Failed to delete: '+(e.message||e)); }
  };

  const openNew = () => { setModal(emptyJob()); setLaborEntries([]); setLaborForm(null); setLogEntries([]); setLogForm(null); };
  const openEdit = j => {
    setModal({...j, clientId:j.clientId||j.client_id||null, estimateId:j.estimateId||j.estimate_id||null, bidId:j.bidId||j.bid_id||null, endDate:j.endDate||j.end_date||'', fieldNotes:j.fieldNotes||j.field_notes||[]});
    setLaborEntries([]); setLaborForm(null); loadLabor(j.id);
    setLogEntries([]); setLogForm(null); loadLog(j.id);
  };

  const save = async () => {
    if (!modal.title.trim()) return;
    setSaving(true);
    try {
      const toSave = {...modal};
      if (toSave.status === 'Needs Scheduling' && toSave.date) {
        toSave.status = 'Scheduled';
      }
      await apiSaveJob(toSave);
      setModal(null);
    } catch(e) { alert("Failed to save: "+e.message); }
    setSaving(false);
  };

  const clientEstimates = clientId => toArr(estimates).filter(e => (e.clientId||e.client_id) === clientId);
  const clientBids = clientId => toArr(bids).filter(b => { const bc=b.clientId||b.client_id; return (!bc||bc===clientId) && b.status==='Won'; });
  const jobExpenses = modal?.id ? toArr(expenses).filter(e => (e.jobId ?? e.linked_job_id) === modal.id) : [];
  const jobExpensesTotal = jobExpenses.reduce((s,e)=>s+(Number(e.amount)||0), 0);
  const linkedEstimate = modal?.id && modal.estimateId ? toArr(estimates).find(e => e.id === modal.estimateId) : null;
  const jobInvoices = modal?.id ? toArr(invoices).filter(i=>(i.job_id||i.jobId)===modal.id && i.status!=='Cancelled') : [];
  const invoiceRevenue = jobInvoices.reduce((s,inv)=>{ try{const it=typeof inv.items==='string'?JSON.parse(inv.items):(inv.items||[]);return s+calcTotal(it);}catch(_){return s;} },0);
  const estimateRevenue = linkedEstimate ? (()=>{ try{const it=typeof linkedEstimate.items==='string'?JSON.parse(linkedEstimate.items):(linkedEstimate.items||[]);return calcTotal(it);}catch(_){return null;} })() : null;
  const revenue = invoiceRevenue > 0 ? invoiceRevenue : estimateRevenue;
  const mr=markup?.masterRate||125,jr=markup?.journeymanRate||95,hr=(markup?.apprenticeRate ?? markup?.helperRate ?? 50);
  const logLaborTotal = logEntries.reduce((s,e)=>s+Number(e.master_hrs)*mr+Number(e.journeyman_hrs)*jr+Number(e.apprentice_hrs)*hr, 0);
  const laborTotal = laborEntries.reduce((s,e) => s + Number(e.hours)*Number(e.rate), 0) + logLaborTotal;
  const grossProfit = (revenue||0) - jobExpensesTotal - laborTotal;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:8}}>
        <div style={st.h1}>Jobs</div>
        {canBill && <button style={st.btn("primary")} onClick={openNew}>+ Add Job</button>}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:14,marginBottom:20}}>
        <div style={st.card}>
          <div style={{fontSize:11,color:D.muted,marginBottom:6}}>Active Jobs</div>
          <div style={{fontSize:22,fontWeight:600}}>{activeJobs.length}</div>
        </div>
        <div style={st.card}>
          <div style={{fontSize:11,color:D.muted,marginBottom:6}}>Scheduled Today</div>
          <div style={{fontSize:22,fontWeight:600,color:D.accent}}>{todayJobs.length}</div>
        </div>
      </div>

      {needsScheduling.length>0 && (
        <div style={{background:STATUS_BG["Needs Scheduling"],border:`1px solid ${STATUS_COLOR["Needs Scheduling"]}`,borderRadius:8,padding:"10px 16px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
          <div style={{color:STATUS_COLOR["Needs Scheduling"],fontWeight:500,fontSize:13}}>
            {needsScheduling.length} new job{needsScheduling.length===1?'':'s'} from approved estimates need{needsScheduling.length===1?'s':''} scheduling
          </div>
          <button style={st.btn("danger")} onClick={()=>setFilter('Needs Scheduling')}>Review</button>
        </div>
      )}

      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
        {['Active','All',...JOB_STATUSES].map(f=>
          <button key={f} style={f===filter?st.btn("primary"):st.btn()} onClick={()=>setFilter(f)}>{f}</button>
        )}
      </div>

      <div style={st.card}>
        <div style={{overflowX:"auto"}}><table style={st.table}>
          <thead><tr>{["Title","Client","Status","Date","Address",""].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
          <tbody>
            {visible.map(j=>(
              <tr key={j.id} style={{cursor:"pointer"}} onClick={()=>openEdit(j)}>
                <td style={st.td}><span style={{fontWeight:500}}>{j.title}</span></td>
                <td style={st.td}>{(j.clientId||j.client_id)?clientName(j.clientId||j.client_id):'-'}</td>
                <td style={st.td}><JobBadge status={j.status}/></td>
                <td style={st.td}>{fmtDate(j.date)}</td>
                <td style={st.td}>{j.address||'-'}</td>
                <td style={st.td} onClick={ev=>ev.stopPropagation()}>
                  <div style={{display:"flex",gap:6}}>
                    {j.address && <button style={{...st.btn(),fontSize:11,padding:'2px 8px'}} onClick={()=>getDirections(j.address)}>Directions</button>}
                    {isOwner && <button style={{...st.btn('danger'),fontSize:11,padding:'2px 8px'}} onClick={()=>{if(confirm('Delete this job?'))apiDeleteJob(j.id);}}>Delete</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
        {visible.length===0 && <div style={{padding:"30px 0",textAlign:"center",color:D.muted,fontSize:13}}>No jobs in this view.</div>}
      </div>

      {modal && <Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(520px,95vw)"}}>
        <MHdr title={modal.id?"Edit Job":"New Job"} onClose={()=>setModal(null)}/>
        {modal.id && openInvoiceModal && (
          <div style={{padding:"0 0 10px",display:"flex",justifyContent:"flex-end"}}>
            <button style={{...st.btn,background:D.accent,color:"#fff",fontSize:13,padding:"6px 16px",borderRadius:6}}
              onClick={()=>{setModal(null);openInvoiceModal(null,null,[],modal.client_id||modal.clientId,modal.id);}}>
              + Create Invoice
            </button>
          </div>
        )}
        <fieldset disabled={!canBill} style={{border:"none",padding:0,margin:0}}>
        {modal.status==="Needs Scheduling" && (
          <div style={{background:STATUS_BG["Needs Scheduling"],border:`1px solid ${STATUS_COLOR["Needs Scheduling"]}`,borderRadius:8,padding:"8px 12px",marginBottom:14,color:STATUS_COLOR["Needs Scheduling"],fontSize:12,fontWeight:500}}>
            This job was auto-created from an approved estimate with a deposit. Set a date/time and update the status once it's scheduled.
          </div>
        )}
        <Field label="Title"><input style={st.input} value={modal.title} onChange={e=>setModal(m=>({...m,title:e.target.value}))}/></Field>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <Field label="Client">
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <select style={{...st.input,flex:1}} value={modal.clientId||''} onChange={e=>setModal(m=>({...m,clientId:e.target.value?Number(e.target.value):null,estimateId:null}))}>
                <option value="">- None -</option>
                {toArr(clients).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button type="button" style={{...st.btn(),whiteSpace:"nowrap",flexShrink:0,background:"#1e3a5f",border:"1px solid #2d5f9e",color:"#7eb8f7"}} onClick={()=>openClientModal(null,(c)=>setModal(m=>({...m,clientId:c.id})))}>+ New Client</button>
            </div>
          </Field>
          <Field label="Linked Estimate">
            <select style={st.input} value={modal.estimateId||''} onChange={e=>setModal(m=>({...m,estimateId:e.target.value?Number(e.target.value):null}))} disabled={!modal.clientId}>
              <option value="">- None -</option>
              {clientEstimates(modal.clientId).map(e=><option key={e.id} value={e.id}>{e.number}</option>)}
            </select>
          </Field>
          <Field label="Linked Bid (Won)">
            <select style={st.input} value={modal.bidId||''} onChange={e=>setModal(m=>({...m,bidId:e.target.value?Number(e.target.value):null}))} disabled={!modal.clientId}>
              <option value="">- None -</option>
              {clientBids(modal.clientId).map(b=><option key={b.id} value={b.id}>{b.number}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select style={st.input} value={modal.status} onChange={e=>setModal(m=>({...m,status:e.target.value}))}>
              {JOB_STATUSES.map(s=><option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Date"><input type="date" style={st.input} value={modal.date} onChange={e=>setModal(m=>({...m,date:e.target.value}))}/></Field>
          <Field label="Time"><input type="time" style={st.input} value={modal.time} onChange={e=>setModal(m=>({...m,time:e.target.value}))}/></Field>
          <Field label="End Date"><input type="date" style={st.input} value={modal.endDate} onChange={e=>setModal(m=>({...m,endDate:e.target.value}))}/></Field>
          <div style={{gridColumn:"1/-1"}}><Field label="Address"><input style={st.input} value={modal.address} onChange={e=>setModal(m=>({...m,address:e.target.value}))}/></Field></div>
          <div style={{gridColumn:"1/-1"}}><Field label="Description"><input style={st.input} value={modal.description} onChange={e=>setModal(m=>({...m,description:e.target.value}))}/></Field></div>
        </div>
        {modal.id && (
          <div style={{marginTop:16,paddingTop:16,borderTop:`1px solid ${D.border}`}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:8}}>Expenses</div>
            {jobExpenses.length===0 ? (
              <div style={{color:D.muted,fontSize:12}}>No expenses logged for this job.</div>
            ) : (
              <div style={{overflowX:"auto"}}><table style={st.table}>
                <thead><tr>{["Date","Category","Description","Amount"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {jobExpenses.map(e=>(
                    <tr key={e.id}>
                      <td style={st.td}>{fmtDate(e.date)}</td>
                      <td style={st.td}>{e.category}</td>
                      <td style={st.td}>{e.description}</td>
                      <td style={st.td}>{fmt(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
            {jobExpenses.length>0 && <div style={{textAlign:"right",fontSize:13,fontWeight:600,marginTop:8}}>Total: {fmt(jobExpensesTotal)}</div>}
          </div>
        )}
        {modal.id && (
          <div style={{marginTop:16,paddingTop:16,borderTop:`1px solid ${D.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:13,fontWeight:500}}>Labor</div>
              {!laborForm && isOwner && jobEmployees.length>0 && (
                <button style={{...st.btn(),padding:"3px 10px",fontSize:11}} onClick={()=>setLaborForm({userId:jobEmployees[0]?.id||'',date:new Date().toISOString().slice(0,10),hours:'',rate:jobEmployees[0]?.rate||'',notes:''})}>+ Add</button>
              )}
            </div>
            {laborLoading ? (
              <div style={{fontSize:12,color:D.muted}}>Loading...</div>
            ) : laborEntries.length===0 && !laborForm ? (
              <div style={{color:D.muted,fontSize:12}}>No labor logged for this job yet.</div>
            ) : (<>
              {laborEntries.length>0 && (
                <div style={{overflowX:"auto"}}><table style={st.table}>
                  <thead><tr>{["Employee","Date","Hours","Rate","Cost"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {laborEntries.map(e=>(
                      <tr key={e.id}>
                        <td style={st.td}>{e.employee_name}</td>
                        <td style={st.td}>{fmtDate(e.date)}</td>
                        <td style={st.td}>{Number(e.hours).toFixed(2)}h</td>
                        <td style={st.td}>${Number(e.rate).toFixed(2)}/hr</td>
                        <td style={st.td}>{fmt(Number(e.hours)*Number(e.rate))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
              {laborEntries.length>0 && <div style={{textAlign:"right",fontSize:13,fontWeight:600,marginTop:8}}>Total Labor: {fmt(laborEntries.reduce((s,e)=>s+Number(e.hours)*Number(e.rate),0))}</div>}
            </>)}
            {laborForm && (
              <div style={{background:D.surface,borderRadius:8,padding:12,marginTop:8,border:`1px solid ${D.border}`}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                  <div>
                    <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Employee</div>
                    <select style={st.input} value={laborForm.userId} onChange={e=>{const emp=jobEmployees.find(u=>u.id===Number(e.target.value));setLaborForm(f=>({...f,userId:e.target.value,rate:emp?.rate||f.rate}));}}>
                      {jobEmployees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Date</div>
                    <input type="date" style={st.input} value={laborForm.date} onChange={e=>setLaborForm(f=>({...f,date:e.target.value}))}/>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Hours</div>
                    <input type="number" step="0.25" min="0" style={st.input} value={laborForm.hours} onChange={e=>setLaborForm(f=>({...f,hours:e.target.value}))} placeholder="0.0"/>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Rate ($/hr)</div>
                    <input type="number" step="0.01" min="0" style={st.input} value={laborForm.rate} onChange={e=>setLaborForm(f=>({...f,rate:e.target.value}))} placeholder="0.00"/>
                  </div>
                </div>
                <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Notes</div>
                <input style={{...st.input,marginBottom:8}} value={laborForm.notes} onChange={e=>setLaborForm(f=>({...f,notes:e.target.value}))} placeholder="Optional"/>
                <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                  <button style={st.btn()} onClick={()=>setLaborForm(null)}>Cancel</button>
                  <button style={st.btn("primary")} onClick={saveLabor} disabled={!laborForm.userId||!laborForm.hours}>Save</button>
                </div>
              </div>
            )}
          </div>
        )}
        {modal.id && (
          <div style={{marginTop:16,paddingTop:16,borderTop:`1px solid ${D.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:13,fontWeight:500}}>Daily Log</div>
              {canBill && !logForm && <button style={{...st.btn(),padding:"3px 10px",fontSize:11}} onClick={()=>setLogForm({date:new Date().toISOString().slice(0,10),description:'',masterHrs:'',journeymanHrs:'',apprenticeHrs:''})}>+ Add Entry</button>}
            </div>
            {logLoading ? <div style={{fontSize:12,color:D.muted}}>Loading...</div> : (<>
              {logEntries.length>0 && (
                <div style={{overflowX:"auto"}}><table style={st.table}>
                  <thead><tr>{["Date","By","Description","M hrs","J hrs","H hrs",""].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {logEntries.map(e=>(
                      <tr key={e.id}>
                        <td style={st.td}>{fmtDate(e.log_date)}</td>
                        <td style={st.td}>{e.user_name||'Owner'}</td>
                        <td style={st.td}>{e.description||'-'}</td>
                        <td style={st.td}>{Number(e.master_hrs)||'-'}</td>
                        <td style={st.td}>{Number(e.journeyman_hrs)||'-'}</td>
                        <td style={st.td}>{Number(e.apprentice_hrs)||'-'}</td>
                        <td style={st.td}>{isOwner && <button style={{...st.btn('danger'),fontSize:11,padding:'2px 8px'}} onClick={()=>deleteLog(e.id)}>Del</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
              {logEntries.length===0 && !logForm && <div style={{color:D.muted,fontSize:12}}>No log entries yet.</div>}
            </>)}
            {logForm && (
              <div style={{background:D.surface,borderRadius:8,padding:12,marginTop:8,border:`1px solid ${D.border}`}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
                  <div style={{gridColumn:"1/-1"}}>
                    <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Date</div>
                    <input type="date" style={st.input} value={logForm.date} onChange={e=>setLogForm(f=>({...f,date:e.target.value}))}/>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Description</div>
                    <input style={st.input} value={logForm.description} onChange={e=>setLogForm(f=>({...f,description:e.target.value}))} placeholder="Work performed today"/>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Master hrs</div>
                    <input type="number" step="0.25" min="0" style={st.input} value={logForm.masterHrs} onChange={e=>setLogForm(f=>({...f,masterHrs:e.target.value}))} placeholder="0"/>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Journeyman hrs</div>
                    <input type="number" step="0.25" min="0" style={st.input} value={logForm.journeymanHrs} onChange={e=>setLogForm(f=>({...f,journeymanHrs:e.target.value}))} placeholder="0"/>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Apprentice hrs</div>
                    <input type="number" step="0.25" min="0" style={st.input} value={logForm.apprenticeHrs} onChange={e=>setLogForm(f=>({...f,apprenticeHrs:e.target.value}))} placeholder="0"/>
                  </div>
                </div>
                <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                  <button style={st.btn()} onClick={()=>setLogForm(null)}>Cancel</button>
                  <button style={st.btn("primary")} onClick={saveLog}>Save</button>
                </div>
              </div>
            )}
          </div>
        )}
        {modal.id && (
          <div style={{marginTop:16,paddingTop:16,borderTop:`1px solid ${D.border}`}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:10}}>Profit Summary</div>
            <div style={{background:D.surface,borderRadius:9,padding:"12px 14px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
                <div style={{fontSize:13,color:D.muted}}>Revenue{!revenue&&<span style={{fontSize:10,marginLeft:6,color:D.muted}}>(link estimate/invoice)</span>}</div>
                <div style={{fontSize:13,fontWeight:600,color:D.text}}>{revenue?fmt(revenue):"—"}</div>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
                <div style={{fontSize:13,color:D.muted}}>Expenses</div>
                <div style={{fontSize:13,fontWeight:600,color:jobExpensesTotal>0?D.danger:D.muted}}>{jobExpensesTotal>0?`−${fmt(jobExpensesTotal)}`:fmt(0)}</div>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
                <div style={{fontSize:13,color:D.muted}}>Labor</div>
                <div style={{fontSize:13,fontWeight:600,color:laborTotal>0?D.danger:D.muted}}>{laborTotal>0?`−${fmt(laborTotal)}`:fmt(0)}</div>
              </div>
              <div style={{borderTop:`1px solid ${D.border}`,marginTop:8,paddingTop:10,display:"flex",justifyContent:"space-between",alignItems:"flex-end"}}>
                <div style={{fontSize:14,fontWeight:600,color:D.text}}>Gross Profit</div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:16,fontWeight:700,color:grossProfit>0?D.success:grossProfit<0?D.danger:D.muted}}>{fmt(grossProfit)}</div>
                  {revenue>0&&grossProfit!==0&&<div style={{fontSize:11,color:D.muted}}>{Math.round(grossProfit/revenue*100)}% margin</div>}
                </div>
              </div>
            </div>
          </div>
        )}
        {modal.id && (
          <div style={{marginTop:16}}>
            <FieldNotesPanel
              notes={modal.fieldNotes}
              onSaveNote={async note => {
                const updated = [...(modal.fieldNotes||[]), note];
                setModal(m=>({...m, fieldNotes:updated}));
                await apiSaveJobNotes(modal.id, updated);
              }}
              onDeleteNote={async noteId => {
                const updated = (modal.fieldNotes||[]).filter(n=>n.id!==noteId);
                setModal(m=>({...m, fieldNotes:updated}));
                await apiSaveJobNotes(modal.id, updated);
              }}
            />
          </div>
        )}
        </fieldset>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:16}}>
          <button style={st.btn()} onClick={()=>setModal(null)}>Cancel</button>
          {canBill && <button style={st.btn("primary")} disabled={saving||!modal.title.trim()} onClick={save}>{saving?'Saving...':'Save'}</button>}
        </div>
      </div></Overlay>}
    </div>
  );
}
